import React, { Fragment, useState, useEffect, useRef, useMemo } from "react";
import { Table, Button, Checkbox, Dropdown, Card } from "antd";
import { SettingOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import formatNumber from "../../../../utils/formatNumber";
import moment from "moment";

const InvoiceDetailsTbl = ({
	invoiceDetailsData,
	isLoading,
	OnhandleTableSortColumn,
	selectAnalysisType,
	selectedDistributionIds,
	selectedCountryIds,
	selectedPriceGroupIds,
	selectedSalesOfficeIds,
	isEmployee,
	isReceivable,
}) => {
	const isFirstRender = useRef(true);
	const companyDetails = useSelector((state) => state.CompanyDetails.companyDetials);
	const globalFilters = useSelector((state) => state.GlobalFilters.globalFilterSapCodes);

	const selectedCustomerData = useSelector((state) => state.PaymentTableFilter.selectedCustomerData);
	const prevSelectedCustomerData = useRef(selectedCustomerData);
	const tableContainerRef = useRef(null);
	const [invoiceTableData, setInvoiceTableData] = useState([]);
	const [pageNo, setPageNo] = useState(1);
	const [pastPageNo, setPastPageNo] = useState(1);
	const [selectedCompany, setSelectedCompany] = useState(companyDetails?.company_Code || "");
	const [columnPreferences, setColumnPreferences] = useState({});
	const [checkSelectAnalysisType, setCheckSelectAnalysisType] = useState(selectAnalysisType);
	const [hoveredResizeColumn, setHoveredResizeColumn] = useState(null);
	const [activeResizeColumn, setActiveResizeColumn] = useState(null);

	const finalInvoiceDataRef = useRef([]);
	const totalRecordsRef = useRef(0);
	const checkPaginationNoDataRef = useRef(0);
	const resizingRef = useRef(null);
	const minColumnWidth = 90;
	const resizeCursor =
		"url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 24 24' fill='none'><path d='M10 4V20' stroke='%23FFFFFF' stroke-width='1.7' stroke-linecap='round'/><path d='M14 4V20' stroke='%23FFFFFF' stroke-width='1.7' stroke-linecap='round'/><path d='M7 12H2' stroke='%23FFFFFF' stroke-width='1.7' stroke-linecap='round'/><path d='M4.5 9.5L2 12L4.5 14.5' stroke='%23FFFFFF' stroke-width='1.7' stroke-linecap='round' stroke-linejoin='round'/><path d='M17 12H22' stroke='%23FFFFFF' stroke-width='1.7' stroke-linecap='round'/><path d='M19.5 9.5L22 12L19.5 14.5' stroke='%23FFFFFF' stroke-width='1.7' stroke-linecap='round' stroke-linejoin='round'/></svg>\") 14 14, col-resize";

	const parseAmount = (value) => parseFloat((value || "0").toString().replace(/,/g, "")) || 0;

	useEffect(() => {
		const handleMouseMove = (event) => {
			const resizeState = resizingRef.current;

			if (!resizeState) {
				return;
			}

			const nextWidth = Math.max(
				resizeState.minWidth,
				resizeState.startWidth + (event.clientX - resizeState.startX),
			);

			setColumnPreferences((prev) => ({
				...prev,
				[resizeState.columnKey]: {
					...prev[resizeState.columnKey],
					width: nextWidth,
				},
			}));
		};

		const handleMouseUp = () => {
			if (resizingRef.current) {
				setActiveResizeColumn(null);
				document.body.style.cursor = "";
				document.body.style.userSelect = "";
				resizingRef.current = null;
			}
		};

		window.addEventListener("mousemove", handleMouseMove);
		window.addEventListener("mouseup", handleMouseUp);

		return () => {
			window.removeEventListener("mousemove", handleMouseMove);
			window.removeEventListener("mouseup", handleMouseUp);
		};
	}, []);

	useEffect(() => {
		if (invoiceDetailsData?.customerInvoiceList?.length > 0) {
			const finalData = invoiceDetailsData?.customerInvoiceList || [];
			finalInvoiceDataRef.current = invoiceDetailsData;

			finalData.forEach((el) => {
				let subTableData = isReceivable ? el.customerAgeingList_Received : el.customerAgeingList;
				(subTableData || []).forEach((subData) => {
					const formattedDate = subData.invoiceDate.replace(/\//g, "");
					const currentSeconds = new Date().getMinutes().toString().padStart(2, "0");
					const invoiceId = `${formattedDate}${subData.invoiceNo}${currentSeconds}`;
					subData.invoiceId = invoiceId;
				});
			});
			if (selectedCustomerData?.length > 0) {
				prevSelectedCustomerData.current = selectedCustomerData;
			}

			const existingKeys = new Set(invoiceTableData.map((item) => item.invoiceId));
			const existingInvoiceNos = new Set();
			let CompanyCode = localStorage.getItem("Company_Code");
			let selectedAnalysisType = localStorage.getItem("selectedAnalysisType");
			let transformedData = [];
			const isSelectedCustomerDataEmpty =
				selectedCustomerData?.length === 0 && prevSelectedCustomerData.current?.length !== 0;
			if (
				selectedCompany !== CompanyCode ||
				(globalFilters && Object.keys(globalFilters)?.length > 0) ||
				checkSelectAnalysisType !== selectedAnalysisType ||
				isSelectedCustomerDataEmpty
			) {
				if (pastPageNo === Number(invoiceDetailsData.currentPage)) {
					setSelectedCompany(CompanyCode);
					setCheckSelectAnalysisType(selectAnalysisType);
					transformedData = [];
					setPageNo(1);
					setPastPageNo(Number(invoiceDetailsData.currentPage));
				} else if (pageNo > pastPageNo) {
					transformedData = [...invoiceTableData];
				}

				prevSelectedCustomerData.current = "";
			} else if (
				selectedDistributionIds.length > 0 ||
				selectedCountryIds.length > 0 ||
				selectedPriceGroupIds.length > 0 ||
				selectedSalesOfficeIds.length > 0
			) {
				if (pastPageNo === Number(invoiceDetailsData.currentPage)) {
					transformedData = [];
					setPageNo(1);
					setPastPageNo(Number(invoiceDetailsData.currentPage));
				} else if (pageNo > pastPageNo) {
					transformedData = [...invoiceTableData];
				}
			} else if (pastPageNo === Number(invoiceDetailsData.currentPage)) {
				transformedData = [];
				setPageNo(1);
				setPastPageNo(Number(invoiceDetailsData.currentPage));
			} else {
				transformedData = selectedCustomerData?.length > 0 ? [] : [...invoiceTableData];
			}

			finalData.forEach((customerData) => {
				const agingList = isReceivable
					? customerData.customerAgeingList_Received
					: customerData.customerAgeingList;
				const totalInvoices = (agingList || []).length;
				const existingcustomerOrganizationData = transformedData.findIndex(
					(item) => item.key === `${customerData.customerOrganization} | ${customerData.customer}`,
				);
				if (existingcustomerOrganizationData !== -1) {
					(agingList || []).forEach((invoice) => {
						if (existingKeys.has(invoice.invoiceId) || existingInvoiceNos.has(invoice.invoiceNo)) {
							return;
						}

						transformedData.push({
							key: `${customerData.customerOrganization} | ${customerData.customer}`,
							customerOrganization: null,
							rowSpan: 0,
							customer: invoice.customer,
							invoiceDate: invoice.invoiceDate || "N/A",
							invoiceNo: invoice.invoiceNo,
							totalPendingAmount_LCY: invoice.totalPendingAmount_LCY,
							totalReceivedAmount_LCY: invoice.totalReceivedAmount_LCY,
							totalInvoiceAmt: invoice.totalInvoiceAmt,
							totalundamt: invoice.totalundamt,
							totalunadjamt: invoice.totalunadjamt,
							totalPendingAmount: invoice.totalPendingAmount,
							totalReceivedAmount: invoice.totalReceivedAmount,
							totalcredit: invoice.totalcredit,
							totalcreditdays: invoice.totalcreditdays,
							totaloverdued: invoice.totaloverdued,
							totalTodaytot: invoice.totalTodaytot,
							totalfirst: invoice.totalfirst,
							totalsecond: invoice.totalsecond,
							totalthird: invoice.totalthird,
							totalfourth: invoice.totalfourth,
							totalfifth: invoice.totalfifth,
							totalsixth: invoice.totalsixth,
							totalseventh: invoice.totalseventh,
							totaleighth: invoice.totaleighth,
							currency: invoice.currency,
							duedt: invoice.duedt,
							knock_off_Date: invoice.knock_off_Date
								? moment(invoice.knock_off_Date).format("DD/MM/YYYY")
								: "N/A",
						});
						existingKeys.add(invoice.invoiceId);
						existingInvoiceNos.add(invoice.invoiceNo);
					});
				} else {
					(agingList || []).forEach((invoice, index) => {
						if (existingKeys.has(invoice.invoiceId) || existingInvoiceNos.has(invoice.invoiceNo)) {
							return;
						}

						transformedData.push({
							key: `${customerData.customerOrganization} | ${customerData.customer}`,
							customerOrganization:
								index === 0 ? `${customerData.customerOrganization} | ${customerData.customer}` : null,
							rowSpan: index === 0 ? totalInvoices + 1 : 0,
							customer: invoice.customer,
							invoiceDate: invoice.invoiceDate || "N/A",
							invoiceNo: invoice.invoiceNo,
							totalPendingAmount_LCY: invoice.totalPendingAmount_LCY,
							totalReceivedAmount_LCY: invoice.totalReceivedAmount_LCY,
							totalInvoiceAmt: invoice.totalInvoiceAmt,
							totalundamt: invoice.totalundamt,
							totalunadjamt: invoice.totalunadjamt,
							totalPendingAmount: invoice.totalPendingAmount,
							totalReceivedAmount: invoice.totalReceivedAmount,
							totalcredit: invoice.totalcredit,
							totalcreditdays: invoice.totalcreditdays,
							totaloverdued: invoice.totaloverdued,
							totalTodaytot: invoice.totalTodaytot,
							totalfirst: invoice.totalfirst,
							totalsecond: invoice.totalsecond,
							totalthird: invoice.totalthird,
							totalfourth: invoice.totalfourth,
							totalfifth: invoice.totalfifth,
							totalsixth: invoice.totalsixth,
							totalseventh: invoice.totalseventh,
							totaleighth: invoice.totaleighth,
							currency: invoice.currency,
							duedt: invoice.duedt,
							knock_off_Date: invoice.knock_off_Date
								? moment(invoice.knock_off_Date).format("MM/DD/YYYY")
								: "",
						});
						existingKeys.add(invoice.invoiceId);
						existingInvoiceNos.add(invoice.invoiceNo);
					});
				}

				transformedData.push({
					key: `${customerData.customerOrganization} | ${customerData.customer}-total`,
					customerOrganization: null,
					rowSpan: 0,
					invoiceDate: "Total",
					invoiceNo: null,
					customer: customerData.customer,
					totalPendingAmount_LCY: customerData.totalPendingAmount_LCY,
					totalReceivedAmount_LCY: customerData.totalReceivedAmount_LCY,
					totalInvoiceAmt: customerData.totalInvoiceAmt,
					totalundamt: customerData.totalundamt,
					totalunadjamt: customerData.totalunadjamt,
					totalPendingAmount: customerData.totalPendingAmount,
					totalReceivedAmount: customerData.totalReceivedAmount,
					totalcredit: customerData.totalcredit,
					totalcreditdays: customerData.totalcreditdays,
					totaloverdued: customerData.totaloverdued,
					totalTodaytot: customerData.totaltodaytot,
					totalfirst: customerData.totalfirst,
					totalsecond: customerData.totalsecond,
					totalthird: customerData.totalthird,
					totalfourth: customerData.totalfourth,
					totalfifth: customerData.totalfifth,
					totalsixth: customerData.totalsixth,
					totalseventh: customerData.totalseventh,
					totaleighth: customerData.totaleighth,
					knock_off_Date: null,
				});
				existingKeys.add(customerData.customerOrganization);
				return transformedData;
			});

			const finalTotalRecords = finalData.reduce((acc, item) => {
				const list = isReceivable ? item.customerAgeingList_Received : item.customerAgeingList;
				return acc + (list || []).length;
			}, 0);
			const filteredTotalData = filterLastTotalEntries(transformedData);

			setInvoiceTableData(filteredTotalData);
			totalRecordsRef.current = Number(invoiceDetailsData.totalRecords);
			if (Number(invoiceDetailsData.currentPage) > 1) {
				if (!isFirstRender.current) {
					checkPaginationNoDataRef.current += Number(finalTotalRecords);
					isFirstRender.current = true;
				}
			} else {
				if (checkSelectAnalysisType !== selectedAnalysisType) {
					checkPaginationNoDataRef.current = Number(finalTotalRecords);
					setPageNo(1);
				} else {
					if (pageNo === Number(invoiceDetailsData.currentPage)) {
						checkPaginationNoDataRef.current = Number(finalTotalRecords);
					} else {
						checkPaginationNoDataRef.current = Number(finalTotalRecords);
					}
				}
			}

			setTimeout(() => {
				isFirstRender.current = true;
			}, 1000);
		} else {
			setInvoiceTableData([]);
			finalInvoiceDataRef.current = [];
		}
	}, [invoiceDetailsData]);

	useEffect(() => {
		if (pageNo !== 1) {
			OnhandleTableSortColumn({
				currentPage: pageNo,
				table: "Invoice Details",
				sortColumn: "invoiceDate",
				orderBy: "asc",
				isPaginationSorting: false,
			});
		}
	}, [pageNo]);

	useEffect(() => {
		const tableBody = tableContainerRef.current?.querySelector(".ant-table-body");

		if (!tableBody) return;

		let prevScrollTop = tableBody.scrollTop || 0;

		const handleScroll = () => {
			const { scrollTop, scrollHeight, clientHeight } = tableBody;

			if (scrollTop > prevScrollTop) {
				handleTableOnScrollData(scrollTop, scrollHeight, clientHeight);
			}

			prevScrollTop = scrollTop;
		};

		tableBody.addEventListener("scroll", handleScroll);

		return () => {
			tableBody.removeEventListener("scroll", handleScroll);
		};
	}, []);

	function filterLastTotalEntries(data) {
		const lastTotalIndexMap = new Map();

		data.forEach((item, index) => {
			if (item.key.endsWith("-total")) {
				lastTotalIndexMap.set(item.key, index);
			}
		});

		return data.filter((item, index) => {
			return !item.key.endsWith("-total") || lastTotalIndexMap.get(item.key) === index;
		});
	}

	const handleTableOnScrollData = (scrollTop, scrollHeight, clientHeight) => {
		const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50;

		if (isNearBottom) {
			if (totalRecordsRef.current > checkPaginationNoDataRef.current) {
				if (isFirstRender.current) {
					setPageNo((prevPageNo) => {
						const nextPage = prevPageNo + 1;
						return nextPage;
					});
					isFirstRender.current = false;
				}
			}
		}
	};

	const columnDefinitions = useMemo(
		() => [
			{
				key: "customerOrganization",
				label: "Customer",
				title: "Customer",
				dataIndex: "customerOrganization",
				width: 150,
				defaultFixed: true,
				render: (text) => {
					return {
						children: text ? <strong>{text}</strong> : null,
					};
				},
			},
			{
				key: "invoiceDate",
				label: "Invoice Date",
				title: "Invoice Date",
				dataIndex: "invoiceDate",
				width: 120,
				defaultFixed: true,
				render: (text) => (text === "Total" ? <strong>{text}</strong> : text),
			},
			{
				key: "invoiceNo",
				label: "Invoice Number",
				title: "Invoice Number",
				dataIndex: "invoiceNo",
				width: 135,
				defaultFixed: true,
			},
			{
				key: "duedt",
				label: "Due Date",
				title: "Due Date",
				dataIndex: "duedt",
				width: 115,
				defaultFixed: true,
			},
			...(isReceivable
				? [
						{
							key: "knock_off_Date",
							label: "Knock-Off Date",
							title: "Knock-Off Date",
							dataIndex: "knock_off_Date",
							width: 150,
							defaultFixed: true,
						},
					]
				: []),
			{
				key: "currency",
				label: "Currency",
				title: "Currency",
				dataIndex: "currency",
				width: 140,
				defaultFixed: true,
			},
			{
				key: "totalInvoiceAmt",
				label: "Invoice Amount",
				title: "Invoice Amount",
				dataIndex: "totalInvoiceAmt",
				width: 200,
				defaultFixed: true,
				render: (_text, record) => `${formatNumber(parseAmount(record.totalInvoiceAmt))}`,
			},
			...(isEmployee
				? [
						{
							key: "totalPendingAmount_LCY",
							label: isReceivable ? "Received Amount LCY" : "Pending Amount LCY",
							title: isReceivable ? "Received Amount LCY" : "Pending Amount LCY",
							dataIndex: isReceivable ? "totalReceivedAmount_LCY" : "totalPendingAmount_LCY",
							width: 160,
							render: (_text, record) => {
								const field = isReceivable ? "totalReceivedAmount_LCY" : "totalPendingAmount_LCY";
								return `${formatNumber(parseAmount(record[field]))}`;
							},
						},
					]
				: []),
			...(!isReceivable
				? [
						{
							key: "totalundamt",
							label: "Undue Amount",
							title: "Undue Amount",
							dataIndex: "totalundamt",
							width: 130,
							render: (_text, record) => `${formatNumber(parseAmount(record.totalundamt))}`,
						},
						{
							key: "totalunadjamt",
							label: "Unadjusted Amount",
							title: "Unadjusted Amount",
							dataIndex: "totalunadjamt",
							width: 170,
							render: (_text, record) => `${formatNumber(parseAmount(record.totalunadjamt))}`,
						},
					]
				: []),
			{
				key: "totalPendingAmount",
				label: isReceivable ? "Received Amount" : "Pending Amount",
				title: isReceivable ? "Received Amount" : "Pending Amount",
				dataIndex: isReceivable ? "totalReceivedAmount" : "totalPendingAmount",
				width: 150,
				render: (_text, record) => {
					const field = isReceivable ? "totalReceivedAmount" : "totalPendingAmount";
					return `${formatNumber(parseAmount(record[field]))}`;
				},
			},
			{
				key: "totalcredit",
				label: "Total Credit Limit",
				title: "Total Credit Limit",
				dataIndex: "totalcredit",
				width: 150,
				render: (_text, record) => `${formatNumber(parseAmount(record.totalcredit))}`,
			},
			{
				key: "totalcreditdays",
				label: "Credit Days",
				title: "Credit Days",
				dataIndex: "totalcreditdays",
				width: 115,
				render: (_i, row) => {
					return <div>{formatNumber(row.totalcreditdays)}</div>;
				},
			},
			{
				key: "totaloverdued",
				label: "Over Due Days",
				title: "Over Due Days",
				dataIndex: "totaloverdued",
				width: 135,
				render: (_i, row) => {
					return <div>{formatNumber(row.totaloverdued)}</div>;
				},
			},
			{
				key: "totalTodaytot",
				label: "Today Total Days",
				title: "Today Total Days",
				dataIndex: "totalTodaytot",
				width: 120,
				render: (_i, row) => {
					return <div>{formatNumber(row.totalTodaytot)}</div>;
				},
			},
			{
				key: "totalfirst",
				label: "0-10 Days",
				title: "0-10 Days",
				dataIndex: "totalfirst",
				width: 115,
				render: (_i, row) => {
					return <div>{row.totalfirst !== "" ? formatNumber(parseAmount(row.totalfirst)) : "-"}</div>;
				},
			},
			{
				key: "totalsecond",
				label: "11-15 Days",
				title: "11-15 Days",
				dataIndex: "totalsecond",
				width: 120,
				render: (_i, row) => {
					return <div>{row.totalsecond !== "" ? formatNumber(parseAmount(row.totalsecond)) : "-"}</div>;
				},
			},
			{
				key: "totalthird",
				label: "16-30 Days",
				title: "16-30 Days",
				dataIndex: "totalthird",
				width: 120,
				render: (_i, row) => {
					return <div>{row.totalthird !== "" ? formatNumber(parseAmount(row.totalthird)) : "-"}</div>;
				},
			},
			{
				key: "totalfourth",
				label: "31-45 Days",
				title: "31-45 Days",
				dataIndex: "totalfourth",
				width: 120,
				render: (_i, row) => {
					return <div>{row.totalfourth !== "" ? formatNumber(parseAmount(row.totalfourth)) : "-"}</div>;
				},
			},
			{
				key: "totalfifth",
				label: "46-60 Days",
				title: "46-60 Days",
				dataIndex: "totalfifth",
				width: 120,
				render: (_i, row) => {
					return <div>{row.totalfifth !== "" ? formatNumber(parseAmount(row.totalfifth)) : "-"}</div>;
				},
			},
			{
				key: "totalsixth",
				label: "61-75 Days",
				title: "61-75 Days",
				dataIndex: "totalsixth",
				width: 120,
				render: (_i, row) => {
					return <div>{row.totalsixth !== "" ? formatNumber(parseAmount(row.totalsixth)) : "-"}</div>;
				},
			},
			{
				key: "totalseventh",
				label: "76-90 Days",
				title: "76-90 Days",
				dataIndex: "totalseventh",
				width: 120,
				render: (_i, row) => {
					return <div>{row.totalseventh !== "" ? formatNumber(parseAmount(row.totalseventh)) : "-"}</div>;
				},
			},
			{
				key: "totaleighth",
				label: "90 Days",
				title: "90 Days",
				dataIndex: "totaleighth",
				width: 120,
				render: (_i, row) => {
					return <div>{row.totaleighth !== "" ? formatNumber(parseAmount(row.totaleighth)) : "-"}</div>;
				},
			},
		],
		[isEmployee, isReceivable],
	);

	useEffect(() => {
		setColumnPreferences((prev) => {
			const next = {};

			columnDefinitions.forEach((column) => {
				next[column.key] = {
					visible: prev[column.key]?.visible ?? true,
					fixed: prev[column.key]?.fixed ?? Boolean(column.defaultFixed),
					width: prev[column.key]?.width ?? column.width,
				};
			});

			return next;
		});
	}, [columnDefinitions]);

	const visibleColumnCount = Object.values(columnPreferences).filter((column) => column.visible).length;
	const allColumnsSelected = visibleColumnCount === columnDefinitions.length;
	const isSelectAllIndeterminate = visibleColumnCount > 0 && visibleColumnCount < columnDefinitions.length;

	const handleColumnVisibilityChange = (columnKey, checked) => {
		if (!checked && visibleColumnCount === 1) {
			return;
		}

		setColumnPreferences((prev) => {
			const next = {
				...prev,
				[columnKey]: {
					...prev[columnKey],
					visible: checked,
					fixed: checked ? (prev[columnKey]?.fixed ?? false) : false,
				},
			};

			if (checked) {
				const visibleColumns = columnDefinitions.filter((column) => next[column.key]?.visible);
				const targetIndex = visibleColumns.findIndex((column) => column.key === columnKey);
				const hasFixedColumnAfterTarget = visibleColumns.some(
					(column, index) => index > targetIndex && next[column.key]?.fixed,
				);

				if (hasFixedColumnAfterTarget) {
					next[columnKey] = {
						...next[columnKey],
						fixed: true,
					};
				}
			}

			return next;
		});
	};

	const handleSelectAllColumns = (checked) => {
		setColumnPreferences((prev) => {
			const next = {};

			columnDefinitions.forEach((column, index) => {
				next[column.key] = {
					...prev[column.key],
					visible: checked ? true : index === 0,
					fixed: checked ? (prev[column.key]?.fixed ?? Boolean(column.defaultFixed)) : index === 0,
				};
			});

			return next;
		});
	};

	const handleColumnFixedChange = (columnKey, checked) => {
		setColumnPreferences((prev) => {
			const visibleColumns = columnDefinitions.filter((column) => prev[column.key]?.visible);
			const targetIndex = visibleColumns.findIndex((column) => column.key === columnKey);

			if (targetIndex === -1) {
				return prev;
			}

			const next = { ...prev };

			visibleColumns.forEach((column, index) => {
				if (checked) {
					if (index <= targetIndex) {
						next[column.key] = {
							...next[column.key],
							fixed: true,
						};
					}
				} else if (index >= targetIndex) {
					next[column.key] = {
						...next[column.key],
						fixed: false,
					};
				}
			});

			return next;
		});
	};

	const handleColumnResizeStart = (event, columnKey) => {
		event.preventDefault();
		event.stopPropagation();

		const currentWidth =
			columnPreferences[columnKey]?.width ??
			columnDefinitions.find((column) => column.key === columnKey)?.width ??
			minColumnWidth;

		resizingRef.current = {
			columnKey,
			startX: event.clientX,
			startWidth: currentWidth,
			minWidth: minColumnWidth,
		};

		setActiveResizeColumn(columnKey);
		document.body.style.cursor = resizeCursor;
		document.body.style.userSelect = "none";
	};

	const visibleColumns = columnDefinitions.filter((column) => columnPreferences[column.key]?.visible);
	const lastFixedVisibleIndex = visibleColumns.reduce((lastIndex, column, index) => {
		return columnPreferences[column.key]?.fixed ? index : lastIndex;
	}, -1);
	const effectiveFixedColumnKeys = new Set(
		visibleColumns.filter((_column, index) => index <= lastFixedVisibleIndex).map((column) => column.key),
	);

	const columns = visibleColumns.map((column) => {
		const width = columnPreferences[column.key]?.width ?? column.width;

		return {
			...column,
			width,
			fixed: effectiveFixedColumnKeys.has(column.key) ? "left" : undefined,
			onHeaderCell: () => ({
				columnKey: column.key,
				width,
				onResizeStart: (event) => handleColumnResizeStart(event, column.key),
			}),
		};
	});

	const fixedLeftOffsets = {};
	let cumulativeLeft = 0;

	columns.forEach((column) => {
		if (column.fixed === "left") {
			fixedLeftOffsets[column.key] = cumulativeLeft;
			cumulativeLeft += column.width || 120;
		}
	});

	const scrollWidth = columns.reduce((total, column) => total + (column.width || 120), 0);

	const resizableTableComponents = {
		header: {
			cell: ({ children, onResizeStart, width, columnKey, ...restProps }) => {
				const isActive = activeResizeColumn === columnKey;
				const isHovered = hoveredResizeColumn === columnKey;

				return (
					<th
						{...restProps}
						style={{
							...restProps.style,
							width,
							minWidth: width,
							position: "relative",
						}}>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								width: "100%",
								minHeight: "100%",
							}}>
							<span
								style={{
									flex: 1,
									minWidth: 0,
									paddingRight: "14px",
								}}>
								{children}
							</span>
						</div>
						<div
							onMouseDown={onResizeStart}
							onMouseEnter={() => setHoveredResizeColumn(columnKey)}
							onMouseLeave={() => setHoveredResizeColumn((current) => (current === columnKey ? null : current))}
							role="presentation"
							style={{
								position: "absolute",
								top: 0,
								right: -6,
								width: "12px",
								height: "100%",
								cursor: resizeCursor,
								zIndex: 3,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}>
							<div
								style={{
									width: isActive ? "3px" : "2px",
									height: isActive ? "68%" : isHovered ? "56%" : "40%",
									borderRadius: "999px",
									background: isActive ? "#3f3e3d" : isHovered ? "#56514d" : "transparent",
									boxShadow: isActive ? "0 0 0 3px rgba(63, 62, 61, 0.2)" : "none",
									transition: "all 0.18s ease",
								}}
							/>
						</div>
					</th>
				);
			},
		},
	};

	const hasSummaryData = Object.keys(finalInvoiceDataRef?.current || {}).length > 0;
	const summaryValueMap = {
		totalInvoiceAmt: hasSummaryData ? formatNumber(finalInvoiceDataRef?.current.totalInvoiceamtTotal) : "0.00",
		totalPendingAmount_LCY: hasSummaryData ? formatNumber(finalInvoiceDataRef?.current.total) : "0.00",
		totalundamt: hasSummaryData ? formatNumber(finalInvoiceDataRef?.current.totalUndamtTotal) : "0.00",
		totalunadjamt: hasSummaryData ? formatNumber(finalInvoiceDataRef?.current.totalUnadjamtTotal) : "0.00",
		totalPendingAmount: hasSummaryData
			? formatNumber(
					isReceivable
						? finalInvoiceDataRef?.current.totalReceivedAmount
						: finalInvoiceDataRef?.current.totalPendingAmount,
				)
			: "0.00",
		totalcredit: hasSummaryData ? formatNumber(finalInvoiceDataRef?.current.totalcreditTotal) : "0.00",
		totalcreditdays: hasSummaryData ? formatNumber(finalInvoiceDataRef?.current.totalCreditdaysTotal) : "0.00",
		totaloverdued: hasSummaryData ? formatNumber(finalInvoiceDataRef?.current.totalOverDuedaysTotal) : "0.00",
		totalTodaytot: hasSummaryData ? formatNumber(finalInvoiceDataRef?.current.totalTodaytotTotal) : "0.00",
		totalfirst: hasSummaryData ? formatNumber(finalInvoiceDataRef?.current.firstTotal) : "0.00",
		totalsecond: hasSummaryData ? formatNumber(finalInvoiceDataRef?.current.secondTotal) : "0.00",
		totalthird: hasSummaryData ? formatNumber(finalInvoiceDataRef?.current.thirdTotal) : "0.00",
		totalfourth: hasSummaryData ? formatNumber(finalInvoiceDataRef?.current.fourthTotal) : "0.00",
		totalfifth: hasSummaryData ? formatNumber(finalInvoiceDataRef?.current.fifthTotal) : "0.00",
		totalsixth: hasSummaryData ? formatNumber(finalInvoiceDataRef?.current.sixthTotal) : "0.00",
		totalseventh: hasSummaryData ? formatNumber(finalInvoiceDataRef?.current.seventhTotal) : "0.00",
		totaleighth: hasSummaryData ? formatNumber(finalInvoiceDataRef?.current.eighthTotal) : "0.00",
	};

	const columnSettingContent = (
		<div
			style={{
				background: "#ffffff",
				border: "1px solid #e3e8ef",
				borderRadius: "8px",
				boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
				width: "400px",
				maxWidth: "calc(100vw - 48px)",
				overflow: "hidden",
			}}
			onClick={(event) => event.stopPropagation()}>
			<div
				style={{
					padding: "14px 16px 12px",
					background: "#ffffff",
					borderBottom: "1px solid #edf1f5",
				}}>
				<div
					style={{
						fontWeight: 600,
						fontSize: "14px",
						color: "#2f3b52",
					}}>
					Customize Columns
				</div>
				<div
					style={{
						fontSize: "12px",
						color: "#7f8da3",
						marginTop: "4px",
						lineHeight: 1.5,
						paddingRight: "28px",
					}}>
					{visibleColumnCount} columns visible. Select columns to show and pin.
				</div>
			</div>
			<div
				style={{
					maxHeight: "360px",
					overflowY: "auto",
					padding: "8px 16px 14px",
				}}>
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "minmax(0, 1fr) 72px",
						padding: "10px 0",
						borderBottom: "1px solid #edf1f5",
						color: "#8a97ab",
						fontSize: "11px",
						fontWeight: 600,
						letterSpacing: "0.04em",
						textTransform: "uppercase",
					}}>
					<span>Column</span>
					<span style={{ textAlign: "center" }}>Fixed</span>
				</div>
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "minmax(0, 1fr) 72px",
						alignItems: "center",
						gap: "16px",
						padding: "12px 0",
						borderBottom: "1px solid #edf1f5",
						background: "#f8fafc",
					}}>
					<Checkbox
						checked={allColumnsSelected}
						indeterminate={isSelectAllIndeterminate}
						onChange={(event) => handleSelectAllColumns(event.target.checked)}>
						<span
							style={{
								color: "#2f3b52",
								fontWeight: 600,
								display: "inline-block",
								lineHeight: 1.4,
								paddingRight: "8px",
							}}>
							Select All
						</span>
					</Checkbox>
					<div />
				</div>
				{columnDefinitions.map((column) => {
					const preference = columnPreferences[column.key] || {
						visible: true,
						fixed: false,
					};

					return (
						<div
							key={column.key}
							style={{
								display: "grid",
								gridTemplateColumns: "minmax(0, 1fr) 72px",
								alignItems: "center",
								gap: "16px",
								padding: "12px 0",
								borderBottom: "1px solid #f1f4f8",
								background: preference.visible ? "#ffffff" : "#fbfcfd",
							}}>
							<Checkbox
								checked={preference.visible}
								onChange={(event) => handleColumnVisibilityChange(column.key, event.target.checked)}>
								<span
									style={{
										color: preference.visible ? "#39465e" : "#98a3b5",
										fontWeight: 500,
										display: "inline-block",
										lineHeight: 1.4,
										wordBreak: "break-word",
										paddingRight: "8px",
									}}>
									{column.label}
								</span>
							</Checkbox>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									width: "100%",
									height: "100%",
									opacity: preference.visible ? 1 : 0.55,
								}}>
								<Checkbox
									checked={effectiveFixedColumnKeys.has(column.key)}
									disabled={!preference.visible}
									onChange={(event) => handleColumnFixedChange(column.key, event.target.checked)}
								/>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);

	return (
		<Fragment>
			<div className="pay_section_3">
				<div className="row payment_tab">
					<div className="col-md-12 d-flex align-items-center justify-content-between">
						<div>
							<label className="analy_type">Invoice Details</label>
							<div
								style={{
									fontSize: "13px",
									color: "#7f8da3",
									marginTop: "4px",
									lineHeight: 1.5,
								}}>
								Note: Columns are resizable. Hover over any column edge and drag to adjust width.
							</div>
						</div>
						<div
							style={{
								display: "flex",
								paddingTop: "8px",
								paddingBottom: "2px",
                paddingRight: "8px",
							}}>
							<Dropdown overlay={columnSettingContent} trigger={["click"]} placement="bottomRight">
								<Button
									icon={<SettingOutlined style={{ marginBottom: "2px" }} />}
									style={{
										height: "40px",
										borderRadius: "50px",
										border: "1px solid #bfbfbf",
										boxShadow: "none",
										fontWeight: 600,
										paddingInline: "22px",
										background: "#ffffff",
										color: "#2f3b52",
										display: "flex",
										alignItems: "center",
									}}>
									Customize Columns
								</Button>
							</Dropdown>
						</div>
					</div>
				</div>
			</div>
			<Card className="pay_section_2">
				<div className="row">
					<div className="col-md-12">
						<Table
							bordered
							components={resizableTableComponents}
							ref={tableContainerRef}
							scroll={{ x: Math.max(scrollWidth, 900), y: 100 * 5 }}
							className="dashboard_tables payment_table"
							dataSource={invoiceTableData}
							loading={isLoading}
							rowKey="uniqueKey"
							columns={columns}
							rowClassName={(record) => (record.invoiceDate === "Total" ? "total-row" : "")}
							pagination={false}
							onScroll={handleTableOnScrollData}
							summary={() => {
								return (
									<Table.Summary.Row className="tbl_summary" style={{ position: "relative" }}>
										{columns.map((column, index) => {
											const isFixedColumn = column.fixed === "left";
											const fixedStyles = isFixedColumn
												? {
														position: "sticky",
														left: fixedLeftOffsets[column.key],
														zIndex: 2,
														background: "#f5f5f5",
													}
												: {};

											let cellValue = null;

											if (column.key === "customerOrganization") {
												cellValue = "Total";
											} else if (summaryValueMap[column.key] !== undefined) {
												cellValue = summaryValueMap[column.key];
											}

											return (
												<Table.Summary.Cell
													key={column.key}
													index={index}
													style={{ fontWeight: "bold", ...fixedStyles }}>
													{cellValue}
												</Table.Summary.Cell>
											);
										})}
									</Table.Summary.Row>
								);
							}}
						/>
					</div>
				</div>
			</Card>
		</Fragment>
	);
};

export default InvoiceDetailsTbl;
